import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Plus } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { useProjects } from '@/hooks/use-projects';
import { useLocations } from '@/hooks/use-locations';
import { usePersons } from '@/hooks/use-persons';
import { useCreateFiledDocument } from '@/hooks/use-filed-documents';
import { handleApiError } from '@/lib/api-client';
import type { FiledDocumentCategory, CreateFiledDocumentPayload } from '@/lib/types';

const CATEGORY_LABELS: Record<FiledDocumentCategory, string> = {
  insurance_certificate: 'Insurance Certificate',
  client_contract: 'Client Contract',
  policy: 'Policy',
  license: 'License',
  permit: 'Permit',
  certificate: 'Certificate',
  other: 'Other',
};

type AttachTarget = '' | 'person' | 'job' | 'location';

export function CreateDocumentPage() {
  const navigate = useNavigate();
  const createMutation = useCreateFiledDocument();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<FiledDocumentCategory>('other');
  const [description, setDescription] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [attachTarget, setAttachTarget] = useState<AttachTarget>('');
  const [attachedId, setAttachedId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const { data: personsData } = usePersons({ page_size: 100 });
  const { data: projectsData } = useProjects({ page_size: 100 }, attachTarget === 'job');
  const { data: locationsData } = useLocations({ page_size: 100 });

  const recordOptions = useMemo(() => {
    if (attachTarget === 'person') {
      return (personsData?.results ?? []).map((person) => ({ id: person.id, label: person.full_name }));
    }
    if (attachTarget === 'job') {
      return (projectsData?.results ?? []).map((project) => ({ id: project.id, label: project.name }));
    }
    if (attachTarget === 'location') {
      return (locationsData?.results ?? []).map((location) => ({ id: location.id, label: location.name }));
    }
    return [];
  }, [attachTarget, projectsData?.results, locationsData?.results, personsData?.results]);

  const resetAttachment = (nextTarget: AttachTarget) => {
    setAttachTarget(nextTarget);
    setAttachedId('');
    clearFieldError('attached_type');
    clearFieldError('attached_id');
  };

  const clearFieldError = (field: string) => {
    setFieldErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const fieldError = (field: string) => fieldErrors[field]?.join(' ');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFieldErrors({});
    setFormError(null);

    if (!title.trim()) {
      setFieldErrors({ title: ['Title is required.'] });
      return;
    }
    if (!file) {
      setFieldErrors({ file: ['Please choose a file to upload.'] });
      return;
    }

    const payload: CreateFiledDocumentPayload = {
      title: title.trim(),
      category,
      description: description.trim(),
      expiry_date: expiryDate || null,
      attached_type: attachTarget,
      attached_id: attachedId ? Number(attachedId) : null,
      file,
    };

    try {
      await createMutation.mutateAsync(payload);
      navigate('/documents');
    } catch (mutationError) {
      const apiError = handleApiError(mutationError);
      const errors = apiError.errors ?? {};
      setFieldErrors(errors);
      const nonFieldErrors = errors.non_field_errors ?? errors.detail;
      if (nonFieldErrors?.length) {
        setFormError(nonFieldErrors.join(' '));
      } else if (Object.keys(errors).length === 0) {
        setFormError(apiError.message);
      }
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Upload document</h1>
          <p className="mt-1 text-sm text-gray-600">
            File a document against the organization or link it to a Person, Project, or Location.
          </p>
        </div>
        <Link to="/documents">
          <Button variant="outline">Back to documents</Button>
        </Link>
      </div>

      <Card>
        <CardContent className="space-y-5 pt-6">
          {formError && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{formError}</div>}

          <form className="space-y-5" onSubmit={handleSubmit}>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(event) => {
                    setTitle(event.target.value);
                    clearFieldError('title');
                  }}
                  aria-invalid={Boolean(fieldError('title'))}
                />
                {fieldError('title') && <p className="text-sm text-red-600">{fieldError('title')}</p>}
              </div>

              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={category}
                  onValueChange={(value) => {
                    setCategory(value as FiledDocumentCategory);
                    clearFieldError('category');
                  }}
                >
                  <SelectTrigger aria-invalid={Boolean(fieldError('category'))}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fieldError('category') && <p className="text-sm text-red-600">{fieldError('category')}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="expiry_date">Expiry date</Label>
                <Input
                  id="expiry_date"
                  type="date"
                  value={expiryDate}
                  onChange={(event) => {
                    setExpiryDate(event.target.value);
                    clearFieldError('expiry_date');
                  }}
                  aria-invalid={Boolean(fieldError('expiry_date'))}
                />
                {fieldError('expiry_date') && <p className="text-sm text-red-600">{fieldError('expiry_date')}</p>}
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="description">Description</Label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(event) => {
                    setDescription(event.target.value);
                    clearFieldError('description');
                  }}
                  rows={4}
                  aria-invalid={Boolean(fieldError('description'))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                {fieldError('description') && <p className="text-sm text-red-600">{fieldError('description')}</p>}
              </div>

              <div className="space-y-2">
                <Label>Attach to</Label>
                <Select
                  value={attachTarget || 'none'}
                  onValueChange={(value) => resetAttachment(value === 'none' ? '' : (value as AttachTarget))}
                >
                  <SelectTrigger aria-invalid={Boolean(fieldError('attached_type'))}>
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="person">Person</SelectItem>
                    <SelectItem value="job">Project</SelectItem>
                    <SelectItem value="location">Location</SelectItem>
                  </SelectContent>
                </Select>
                {fieldError('attached_type') && <p className="text-sm text-red-600">{fieldError('attached_type')}</p>}
              </div>

              {attachTarget && (
                <div className="space-y-2">
                  <Label>{attachTarget === 'person' ? 'Person' : attachTarget === 'job' ? 'Project' : 'Location'}</Label>
                  <Select
                    value={attachedId}
                    onValueChange={(value) => {
                      setAttachedId(value);
                      clearFieldError('attached_id');
                    }}
                  >
                    <SelectTrigger aria-invalid={Boolean(fieldError('attached_id'))}>
                      <SelectValue placeholder="Choose a record" />
                    </SelectTrigger>
                    <SelectContent>
                      {recordOptions.map((record) => (
                        <SelectItem key={record.id} value={String(record.id)}>
                          {record.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {fieldError('attached_id') && <p className="text-sm text-red-600">{fieldError('attached_id')}</p>}
                </div>
              )}

              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="file">File</Label>
                <Input
                  id="file"
                  type="file"
                  onChange={(event) => {
                    setFile(event.target.files?.[0] ?? null);
                    clearFieldError('file');
                  }}
                  aria-invalid={Boolean(fieldError('file'))}
                />
                {fieldError('file') && <p className="text-sm text-red-600">{fieldError('file')}</p>}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3">
              <Link to="/documents">
                <Button type="button" variant="outline">Cancel</Button>
              </Link>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                Upload document
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
